"use client";
import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { X, Check } from "lucide-react";
import { createPortal } from "react-dom";
import { useToast } from "../../context/ToastContext";

const CropModal = ({ imageFile, onSave, onCancel }) => {
  const { showError } = useToast();
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [imageFile]);

  const initializeCrop = useCallback((img) => {
    if (!img) return;
    const { naturalWidth, naturalHeight, width, height } = img;
    const displayWidth = width || naturalWidth;
    const displayHeight = height || naturalHeight;
    if (!displayWidth || !displayHeight) return;

    const smallerSide = Math.min(displayWidth, displayHeight);
    const initialCrop = makeAspectCrop(
      {
        unit: "%",
        width: (smallerSide / displayWidth) * 100,
      },
      1,
      displayWidth,
      displayHeight,
    );
    const centered = centerCrop(initialCrop, displayWidth, displayHeight);
    setCrop(centered);
    setCompletedCrop(convertToPixelCrop(centered, displayWidth, displayHeight));
  }, []);

  const onImageLoad = useCallback(
    (e) => {
      initializeCrop(e.currentTarget);
    },
    [initializeCrop],
  );

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      initializeCrop(imgRef.current);
    }
  }, [imageUrl, initializeCrop]);

  const getCroppedImg = useCallback(
    (image, crop) => {
      const canvas = canvasRef.current;
      if (!canvas || !crop || !image) {
        return Promise.reject(new Error("Missing canvas, image, or crop data"));
      }

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return Promise.reject(new Error("Unable to obtain canvas 2D context"));
      }

      const sourceX = Math.max(0, crop.x * scaleX);
      const sourceY = Math.max(0, crop.y * scaleY);
      const sourceWidth = Math.min(
        image.naturalWidth - sourceX,
        crop.width * scaleX,
      );
      const sourceHeight = Math.min(
        image.naturalHeight - sourceY,
        crop.height * scaleY,
      );

      if (sourceWidth <= 0 || sourceHeight <= 0) {
        return Promise.reject(new Error("Invalid crop dimensions"));
      }

      // Cap maximum output resolution to 1024x1024 (high-res profile picture, mobile memory safe)
      const MAX_OUTPUT_SIZE = 1024;
      let targetWidth = sourceWidth;
      let targetHeight = sourceHeight;

      if (targetWidth > MAX_OUTPUT_SIZE || targetHeight > MAX_OUTPUT_SIZE) {
        if (targetWidth >= targetHeight) {
          targetHeight = Math.round((targetHeight * MAX_OUTPUT_SIZE) / targetWidth);
          targetWidth = MAX_OUTPUT_SIZE;
        } else {
          targetWidth = Math.round((targetWidth * MAX_OUTPUT_SIZE) / targetHeight);
          targetHeight = MAX_OUTPUT_SIZE;
        }
      }

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.clearRect(0, 0, targetWidth, targetHeight);

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        targetWidth,
        targetHeight,
      );

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const fileName = (imageFile?.name || "profile.jpg").replace(
                /\.[^/.]+$/,
                "_cropped.jpg",
              );
              const croppedFile = new File([blob], fileName, {
                type: "image/jpeg",
              });
              resolve(croppedFile);
            } else {
              reject(new Error("Failed to generate cropped image blob"));
            }
          },
          "image/jpeg",
          0.9,
        );
      });
    },
    [imageFile],
  );

  const handleSave = async () => {
    if (!imgRef.current) return;
    setIsProcessing(true);
    try {
      let cropToUse = completedCrop;
      if (!cropToUse || !cropToUse.width || !cropToUse.height) {
        const { width, height, naturalWidth, naturalHeight } = imgRef.current;
        const displayWidth = width || naturalWidth;
        const displayHeight = height || naturalHeight;
        const smallerSide = Math.min(displayWidth, displayHeight);
        const initialCrop = makeAspectCrop(
          {
            unit: "%",
            width: (smallerSide / displayWidth) * 100,
          },
          1,
          displayWidth,
          displayHeight,
        );
        const centered = centerCrop(initialCrop, displayWidth, displayHeight);
        cropToUse = convertToPixelCrop(centered, displayWidth, displayHeight);
      }

      const croppedFile = await getCroppedImg(imgRef.current, cropToUse);
      if (croppedFile) {
        onSave(croppedFile);
      }
    } catch (error) {
      console.error("Error cropping image:", error);
      showError("Could not process image. Please try again or select another image.");
    } finally {
      setIsProcessing(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[200] p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b">
          <h3 className="text-base sm:text-lg font-semibold">
            Crop Profile Picture
          </h3>
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
          {/* Image area */}
          <div className="flex-1 p-3 sm:p-4 overflow-auto">
            <div className="flex items-center justify-center min-h-[200px] sm:min-h-[300px] lg:min-h-[400px]">
              {imageUrl && (
                <ReactCrop
                  crop={crop}
                  onChange={(newCrop) => setCrop(newCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  className="max-w-full max-h-[50vh] sm:max-h-[60vh] lg:max-h-[70vh]"
                >
                  <img
                    ref={imgRef}
                    src={imageUrl || "/placeholder.svg"}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    onError={() => {
                      showError("Failed to load image preview. Please try another photo.");
                    }}
                    className="max-w-full max-h-full object-contain select-none"
                  />
                </ReactCrop>
              )}
            </div>
          </div>

          {/* Preview and controls */}
          <div className="lg:w-72 xl:w-80 p-3 sm:p-4 border-t lg:border-t-0 lg:border-l bg-gray-50">
            <div className="space-y-3 sm:space-y-4">
              <div>
                <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Preview
                </h4>
                <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 mx-auto bg-white rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center">
                  {completedCrop && imgRef.current ? (
                    <canvas
                      ref={(el) => {
                        if (el && completedCrop && imgRef.current) {
                          const image = imgRef.current;
                          const crop = completedCrop;
                          const canvas = el;
                          const scaleX = image.naturalWidth / image.width;
                          const scaleY = image.naturalHeight / image.height;
                          const ctx = canvas.getContext("2d");
                          if (!ctx) return;
                          const PREVIEW_SIZE = 128;
                          canvas.width = PREVIEW_SIZE;
                          canvas.height = PREVIEW_SIZE;
                          ctx.imageSmoothingEnabled = true;
                          ctx.imageSmoothingQuality = "high";
                          ctx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
                          ctx.drawImage(
                            image,
                            crop.x * scaleX,
                            crop.y * scaleY,
                            crop.width * scaleX,
                            crop.height * scaleY,
                            0,
                            0,
                            PREVIEW_SIZE,
                            PREVIEW_SIZE,
                          );
                        }
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    <div className="text-gray-400 text-xs sm:text-sm">
                      Preview
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                <p>• Drag the corners to resize the crop area</p>
                <p>• Drag the center to move the crop area</p>
                <p>• The preview shows your final profile picture</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 border-t bg-gray-50">
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isProcessing}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm bg-[#FF4C18] text-white rounded-lg hover:bg-[#e03d0d] active:bg-[#c9350a] transition-colors flex items-center gap-1.5 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            {isProcessing ? "Processing..." : "Use This Image"}
          </button>
        </div>
      </div>

      {/* Hidden canvas for processing */}
      <canvas ref={canvasRef} className="hidden" />
    </div>,
    document.body,
  );
};

export default CropModal;
